import { useState, useEffect } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Send, CheckCircle, Clock, AlertCircle, Mail, Settings, Eye, Calendar } from "lucide-react"
import { Pagination } from "@/components/pagination"
import { toast } from "sonner"
import { loadCrawlResults, convertToReportItem } from "@/lib/crawl-data"

interface DeliveryReport {
  id: string
  date: string
  scheduledTime: string
  status: "success" | "failed" | "pending"
  platforms: PlatformDelivery[]
  totalReports: number
  sentAt?: string
}

interface PlatformDelivery {
  platform: string
  email: string
  status: "sent" | "failed"
  reportCount: number
  accounts: Array<{
    id: string
    accountName: string
    accountUrl: string
    keywords: string[]
    verifiedAt: string
  }>
  sentAt?: string
  error?: string
}

interface BroadcastConfig {
  scheduleTime: string
  platforms: Array<{
    name: string
    email: string
    enabled: boolean
  }>
}

const ITEMS_PER_PAGE = 5

export default function BroadcastPage() {
  const [deliveryReports, setDeliveryReports] = useState<DeliveryReport[]>([])
  const [selectedReport, setSelectedReport] = useState<DeliveryReport | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [config, setConfig] = useState<BroadcastConfig>({
    scheduleTime: "09:00",
    platforms: [
      { name: "Instagram", email: "abuse@instagram.com", enabled: true },
      { name: "Facebook", email: "abuse@facebook.com", enabled: true },
      { name: "Twitter", email: "abuse@twitter.com", enabled: true },
      { name: "TikTok", email: "abuse@tiktok.com", enabled: true },
      { name: "YouTube", email: "abuse@youtube.com", enabled: true },
      { name: "Website", email: "abuse@example.com", enabled: true },
    ],
  })

  useEffect(() => {
    // Load delivery reports from localStorage or generate mock data
    const savedReports = localStorage.getItem("broadcast_delivery_reports")
    if (savedReports) {
      setDeliveryReports(JSON.parse(savedReports))
    } else {
      // Generate mock delivery reports
      generateMockDeliveryReports()
    }
  }, [])

  const generateMockDeliveryReports = async () => {
    // Load verified reports from localStorage (from Daily Reports page)
    const verifiedReportsStr = localStorage.getItem("verified_reports")
    let verifiedReports: any[] = []
    
    if (verifiedReportsStr) {
      verifiedReports = JSON.parse(verifiedReportsStr)
    } else {
      // Fallback: load from crawl results and mark as verified
      const data = await loadCrawlResults()
      verifiedReports = data.results
        .map((result, index) => convertToReportItem(result, index))
        .filter((_, idx) => idx < 10) // Take first 10 as verified
        .map(report => ({ ...report, status: "verified" }))
    }
    
    // Group reports by platform
    const platformGroups: Record<string, typeof verifiedReports> = {}
    verifiedReports.forEach(report => {
      if (!platformGroups[report.platform]) {
        platformGroups[report.platform] = []
      }
      platformGroups[report.platform].push(report)
    })

    // Create delivery reports for last 7 days
    const mockReports: DeliveryReport[] = []
    for (let i = 0; i < 7; i++) {
      const date = new Date()
      date.setDate(date.getDate() - i)
      const dateStr = date.toISOString().split('T')[0]
      
      const platformDeliveries: PlatformDelivery[] = config.platforms
        .filter(p => p.enabled)
        .map(platform => {
          const platformReports = platformGroups[platform.name] || []
          const sampleReports = platformReports.slice(0, Math.min(5, platformReports.length))
          
          return {
            platform: platform.name,
            email: platform.email,
            status: i < 2 ? "sent" : "failed",
            reportCount: sampleReports.length,
            accounts: sampleReports.map(report => ({
              id: report.id,
              accountName: report.accountName,
              accountUrl: report.accountUrl,
              keywords: report.detectedKeywords,
              verifiedAt: report.detectedAt,
            })),
            sentAt: i < 2 ? `${dateStr} ${config.scheduleTime}` : undefined,
            error: i >= 2 ? "Email server timeout" : undefined,
          }
        })

      mockReports.push({
        id: `report-${i + 1}`,
        date: dateStr,
        scheduledTime: config.scheduleTime,
        status: i < 2 ? "success" : "failed",
        platforms: platformDeliveries,
        totalReports: platformDeliveries.reduce((sum, p) => sum + p.reportCount, 0),
        sentAt: i < 2 ? `${dateStr} ${config.scheduleTime}` : undefined,
      })
    }

    setDeliveryReports(mockReports)
    localStorage.setItem("broadcast_delivery_reports", JSON.stringify(mockReports))
  }

  const handleSaveConfig = () => {
    localStorage.setItem("broadcast_config", JSON.stringify(config))
    toast.success("Configuration saved", {
      description: "Broadcast settings have been updated",
    })
  }

  const handleTogglePlatform = (index: number) => {
    const updated = [...config.platforms]
    updated[index].enabled = !updated[index].enabled
    setConfig({ ...config, platforms: updated })
  }

  const handleUpdateEmail = (index: number, email: string) => {
    const updated = [...config.platforms]
    updated[index].email = email
    setConfig({ ...config, platforms: updated })
  }

  const filteredReports = deliveryReports
  const totalPages = Math.ceil(filteredReports.length / ITEMS_PER_PAGE)
  const paginatedReports = filteredReports.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  )

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      success: "bg-green-100 text-green-800",
      failed: "bg-red-100 text-red-800",
      pending: "bg-yellow-100 text-yellow-800",
    }
    return colors[status] || "bg-gray-100 text-gray-800"
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "success":
        return <CheckCircle className="h-4 w-4 text-green-600" />
      case "failed":
        return <AlertCircle className="h-4 w-4 text-red-600" />
      case "pending":
        return <Clock className="h-4 w-4 text-yellow-600" />
      default:
        return null
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Broadcast Reports</h1>
        <p className="text-muted-foreground mt-2">
          Laporan pengiriman email otomatis ke admin platform setiap hari
        </p>
      </div>

      <Tabs defaultValue="delivery" className="space-y-4">
        <TabsList>
          <TabsTrigger value="delivery">
            <Mail className="h-4 w-4 mr-2" />
            Delivery Reports
          </TabsTrigger>
          <TabsTrigger value="config">
            <Settings className="h-4 w-4 mr-2" />
            Configuration
          </TabsTrigger>
        </TabsList>

        {/* Delivery Reports Tab */}
        <TabsContent value="delivery" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Email Delivery History</CardTitle>
              <CardDescription>
                Daftar laporan pengiriman email ke admin platform
              </CardDescription>
            </CardHeader>
            <CardContent>
              {paginatedReports.length > 0 ? (
                <div className="space-y-4">
                  {paginatedReports.map((report) => (
                    <div
                      key={report.id}
                      className="border rounded-lg p-4 hover:bg-muted/50 transition-colors cursor-pointer"
                      onClick={() => {
                        setSelectedReport(report)
                        setDialogOpen(true)
                      }}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <Calendar className="h-4 w-4 text-muted-foreground" />
                            <span className="font-semibold text-foreground">
                              {new Date(report.date).toLocaleDateString('id-ID', {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </span>
                            <Badge className={getStatusColor(report.status)}>
                              {getStatusIcon(report.status)}
                              <span className="ml-1">{report.status}</span>
                            </Badge>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3">
                            <div>
                              <p className="text-xs text-muted-foreground">Scheduled Time</p>
                              <p className="text-sm font-medium text-foreground">{report.scheduledTime}</p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">Total Reports</p>
                              <p className="text-sm font-medium text-foreground">{report.totalReports}</p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">Platforms</p>
                              <p className="text-sm font-medium text-foreground">{report.platforms.length}</p>
                            </div>
                            {report.sentAt && (
                              <div>
                                <p className="text-xs text-muted-foreground">Sent At</p>
                                <p className="text-sm font-medium text-foreground">{report.sentAt}</p>
                              </div>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-2 mt-3">
                            {report.platforms.map((platform) => (
                              <Badge
                                key={platform.platform}
                                variant={platform.status === "sent" ? "default" : "destructive"}
                                className="text-xs"
                              >
                                {platform.platform} ({platform.reportCount})
                              </Badge>
                            ))}
                          </div>
                        </div>
                        <Button variant="ghost" size="sm">
                          <Eye className="h-4 w-4 mr-2" />
                          View Details
                        </Button>
                      </div>
                    </div>
                  ))}

                  {totalPages > 1 && (
                    <div className="flex justify-center py-4">
                      <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                      />
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">No delivery reports yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Configuration Tab */}
        <TabsContent value="config" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Broadcast Configuration</CardTitle>
              <CardDescription>
                Atur jadwal pengiriman dan alamat email tujuan untuk setiap platform
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Schedule Time */}
              <div className="space-y-2">
                <Label htmlFor="schedule-time">Schedule Time (Daily)</Label>
                <Input
                  id="schedule-time"
                  type="time"
                  value={config.scheduleTime}
                  onChange={(e) => setConfig({ ...config, scheduleTime: e.target.value })}
                  className="max-w-xs"
                />
                <p className="text-xs text-muted-foreground">
                  Email akan dikirim otomatis setiap hari pada jam yang ditentukan
                </p>
              </div>

              {/* Platform Email Configuration */}
              <div className="space-y-4">
                <Label>Platform Email Addresses</Label>
                <div className="space-y-3">
                  {config.platforms.map((platform, index) => (
                    <div
                      key={platform.name}
                      className="flex items-center gap-4 p-4 border rounded-lg"
                    >
                      <input
                        type="checkbox"
                        checked={platform.enabled}
                        onChange={() => handleTogglePlatform(index)}
                        className="w-4 h-4 cursor-pointer"
                      />
                      <div className="flex-1">
                        <Label className="font-medium text-foreground">{platform.name}</Label>
                        <Input
                          type="email"
                          value={platform.email}
                          onChange={(e) => handleUpdateEmail(index, e.target.value)}
                          placeholder="admin@platform.com"
                          className="mt-1"
                          disabled={!platform.enabled}
                        />
                      </div>
                      <Badge variant={platform.enabled ? "default" : "secondary"}>
                        {platform.enabled ? "Enabled" : "Disabled"}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>

              <Button onClick={handleSaveConfig} className="w-full bg-primary hover:bg-primary/90">
                <Settings className="h-4 w-4 mr-2" />
                Save Configuration
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Detail Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          {selectedReport && (
            <>
              <DialogHeader>
                <DialogTitle>
                  Delivery Report - {new Date(selectedReport.date).toLocaleDateString('id-ID')}
                </DialogTitle>
                <DialogDescription>
                  Detail pengiriman email ke semua platform
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4 p-4 bg-muted rounded-lg">
                  <div>
                    <p className="text-xs text-muted-foreground">Scheduled Time</p>
                    <p className="text-sm font-medium text-foreground">{selectedReport.scheduledTime}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Total Reports</p>
                    <p className="text-sm font-medium text-foreground">{selectedReport.totalReports}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Status</p>
                    <Badge className={getStatusColor(selectedReport.status)}>
                      {selectedReport.status}
                    </Badge>
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="font-semibold text-foreground">Platform Details</h4>
                  {selectedReport.platforms.map((platform) => (
                    <Card key={platform.platform}>
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-lg">{platform.platform}</CardTitle>
                          <div className="flex items-center gap-2">
                            <Badge
                              variant={platform.status === "sent" ? "default" : "destructive"}
                            >
                              {platform.status}
                            </Badge>
                            <Badge variant="secondary">{platform.reportCount} reports</Badge>
                          </div>
                        </div>
                        <CardDescription>
                          Sent to: <span className="font-mono">{platform.email}</span>
                          {platform.sentAt && (
                            <span className="ml-2">at {platform.sentAt}</span>
                          )}
                        </CardDescription>
                        {platform.error && (
                          <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-800">
                            Error: {platform.error}
                          </div>
                        )}
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          <h5 className="font-medium text-sm text-foreground">Accounts in Report:</h5>
                          <div className="space-y-2 max-h-60 overflow-y-auto">
                            {platform.accounts.map((account) => (
                              <div
                                key={account.id}
                                className="p-3 border rounded-lg bg-muted/50"
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <p className="font-medium text-sm text-foreground">
                                      {account.accountName}
                                    </p>
                                    <p className="text-xs text-muted-foreground truncate">
                                      {account.accountUrl}
                                    </p>
                                    <div className="flex flex-wrap gap-1 mt-2">
                                      {account.keywords.map((keyword, idx) => (
                                        <Badge key={idx} variant="secondary" className="text-xs">
                                          {keyword}
                                        </Badge>
                                      ))}
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-1">
                                      Verified: {account.verifiedAt}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
